/**
 * 登录态管理（AuthProvider）— 参照 admin stores/auth.tsx 模式
 * token 存 localStorage['yunniuji_trade_token']，启动时恢复；支持 redirect 回跳
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMe } from '@/api/auth';
import { TOKEN_KEY, USER_KEY } from '@/api/http';
import type { LoginResult, User } from '@/types';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /** 登录成功（写入 token + user） */
  login: (result: LoginResult) => void;
  /** 手动设置用户（注册后自动登录等场景） */
  setUser: (user: User) => void;
  /** 登出（清除本地状态） */
  logout: () => void;
  /** 刷新用户信息 */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(getStoredToken);
  const [user, setUserState] = useState<User | null>(getStoredUser);

  const login = useCallback((result: LoginResult) => {
    try {
      localStorage.setItem(TOKEN_KEY, result.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    } catch {
      /* ignore */
    }
    setToken(result.access_token);
    setUserState(result.user);
  }, []);

  const setUser = useCallback((newUser: User) => {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    } catch {
      /* ignore */
    }
    setUserState(newUser);
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
    setUserState(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      /* 未登录或网络异常时静默 */
    }
  }, [setUser]);

  // 启动时若已有 token，尝试拉取最新用户信息（失败静默，保留本地缓存）
  useEffect(() => {
    if (getStoredToken() && !getStoredUser()) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthState>(
    () => ({ token, user, isAuthenticated: !!token, login, setUser, logout, refreshUser }),
    [token, user, login, setUser, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 同文件同时导出组件与 Hook（admin 同款模式）；关闭 Fast Refresh 组件导出限制
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
